import type { GccDashboardData, GccStage0Result, Project } from '@shared/types'
import JSZip from 'jszip'
import { GCC_AGENT_CATALOG_UNAVAILABLE_NOTE, GCC_AGENT_ESTIMATE_LABEL, GCC_AUTOMATE_FIRST_LABEL, GCC_PATHWAY_LABELS, GCC_QUICK_WIN_LABEL, GCC_QUICK_WIN_OFF_SUMMARY, GCC_WAVES, GCC_WAVE_BANDS, gccExecutivePaletteCss, injectPasswordGate } from '@tn/shared'
import { filterExcludedRegister, filterExcludedTaggedTasks } from './s0bExclusions'
import { GCC_EXPORT_CSS } from './gccExportTheme'
import { GCC_EXPORT_STORY_JS } from './gccExportStory'
import { collectTnAnalyses, generateGccCombinedReport } from './gccCombinedReport'
import { GCC_TOOLTIPS } from './gcc-tooltips'
import { gccAgentBuilds, toSimpleGccData } from './gccSimpleExport'
import { gccWorkView } from './gccWorkView'
import { gccRoleEvolution } from './gccRoleEvolution'

/** The full export, or the Simple (client) one without the internal detail tabs (WI 2822). */
type GccExportVariant = 'full' | 'simple'

/** The Value Case tab's tooltip copy, shared with the app so both say the same thing (WI 2577). */
const VALUE_CASE_TIPS = Object.fromEntries(
  Object.entries(GCC_TOOLTIPS).filter(([key]) => key.startsWith('valueCase') || key === 'sourcingSplit'),
)

function escapeCsvCell(s: string | number): string {
  const str = String(s)
  // Prevent spreadsheet formula injection (Excel/Sheets treats =, +, -, @, tab, CR as formula triggers)
  const safe = /^[=+\-@\t\r]/.test(str) ? `'${str}` : str
  if (safe.includes(',') || safe.includes('"') || safe.includes('\n') || safe.includes('\r')) {
    return `"${safe.replace(/"/g, '""')}"`
  }
  return safe
}

/**
 * UTF-8 byte-order mark. Excel opens a BOM-less CSV as Windows-1252, which turns
 * characters such as "—" into "â€”"; the BOM makes it read the file as UTF-8.
 */
const UTF8_BOM = '\uFEFF'

type RegisterProcess = GccStage0Result['taskRegister'][number]

/** True when the register is a v2 baseline (roles with FTE, no bucket — WI 2323). */
function isBaselineRegister(register: RegisterProcess[]): boolean {
  return register.some(proc => Array.isArray(proc.roles))
}

function fteSourceOf(proc: RegisterProcess, roleTitle: string): string {
  return proc.roles?.find(r => r.roleTitle === roleTitle)?.fteSource ?? ''
}

/**
 * CSV export of the task register, leaving out tasks excluded in the S0b review.
 * v2 baselines export task FTE and its source instead of the v1 automation bucket,
 * because Step 0 no longer classifies work (WI 2323).
 */
export function generateTaskRegisterCsv(gcc: GccDashboardData): string {
  const register = filterExcludedRegister(gcc)
  const baseline = isBaselineRegister(gcc.taskRegister?.taskRegister ?? [])
  const header = baseline
    ? ['Process', 'Function', 'Task', 'Role', 'Time %', 'Task FTE', 'FTE Source', 'Source', 'Task ID']
    : ['Process', 'Function', 'Task', 'Role', 'Time %', 'Automation Bucket', 'Source']
  const rows: string[][] = [header]
  for (const proc of register) {
    for (const task of proc.tasks) {
      const common = [proc.processName, proc.functionScope, task.name, task.roleTitle, String(task.timePct)]
      rows.push(baseline
        ? [...common, String(task.fte ?? ''), fteSourceOf(proc, task.roleTitle), task.source ?? 'LLM', task.taskId ?? '']
        : [...common, task.automationBucket ?? '', task.source ?? 'LLM'])
    }
  }
  return UTF8_BOM + rows.map(row => row.map(escapeCsvCell).join(',')).join('\r\n')
}

/**
 * JSON export of the task register (same rows as the CSV, as an array). Absent fields are left out.
 * No BOM: RFC 8259 forbids one.
 */
export function generateTaskRegisterJson(gcc: GccDashboardData): string {
  const tasks = filterExcludedRegister(gcc).flatMap(proc =>
    proc.tasks.map(task => ({
      ...(task.taskId !== undefined && { taskId: task.taskId }),
      processName: proc.processName,
      functionScope: proc.functionScope,
      taskName: task.name,
      roleTitle: task.roleTitle,
      timePct: task.timePct,
      ...(task.fte !== undefined && { fte: task.fte, fteSource: fteSourceOf(proc, task.roleTitle) }),
      ...(task.automationBucket !== undefined && { automationBucket: task.automationBucket }),
      source: task.source ?? 'LLM',
    }))
  )
  return JSON.stringify({ taskRegister: tasks }, null, 2)
}

function escapeJson(obj: unknown): string {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** PBKDF2 hash and salt of a download's password (WI 2506): one per download, used for every HTML in it. */
export interface GccExportGate {
  hash: string
  salt: string
}

const GCC_GATE_LABELS = {
  logo: 'GCC',
  title: 'Report Protected',
  subtitle: 'Enter the password to view this report',
  button: 'Unlock Report',
}

/** The HTML behind the core TN password gate, or unchanged when the download has no password. */
export function protectGccHtml(html: string, gate?: GccExportGate | null): string {
  return gate ? injectPasswordGate(html, gate.hash, gate.salt, GCC_GATE_LABELS) : html
}

/**
 * Build a ZIP bundle containing the GCC dashboard HTML, task register CSV/JSON,
 * an optional TN analysis HTML per linked project, and the combined report with
 * all of them in one file (WI 2486). Each TN analysis is fetched once.
 *
 * With a gate (WI 2506) every HTML file is password-protected, and the CSV/JSON are
 * left out because an HTML gate cannot protect them.
 *
 * @param gcc              GCC dashboard data
 * @param project          Project metadata (name, branding, derivedTnProjectIds)
 * @param getTnHtml        Optional async resolver: projectId → TN dashboard HTML string
 * @param tnProjects       Optional linked TN projects, used to give combined dashboards and
 *                         scenario analyses readable file names
 * @param gate             Optional password gate for every HTML file in the bundle
 * @returns                Blob ready for URL.createObjectURL() download
 */
export async function generateGccBundle(
  gcc: GccDashboardData,
  project: Project,
  getTnHtml?: (projectId: string) => Promise<string>,
  tnProjects: Pick<Project, 'id' | 'gccCombined' | 'gccScenario'>[] = [],
  gate?: GccExportGate | null,
): Promise<Blob> {
  const zip = new JSZip()
  const slug = (project.client ?? 'gcc').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'gcc'

  const gccHtml = generateGccHtml(gcc, project)
  const analyses = await collectTnAnalyses(project, getTnHtml, tnProjects)

  // The combined report embeds the unprotected views: its own gate covers them
  zip.file(`${slug}-gcc-dashboard.html`, protectGccHtml(gccHtml, gate))
  zip.file(`${slug}-gcc-combined-report.html`, protectGccHtml(generateGccCombinedReport(gccHtml, project, analyses), gate))
  if (!gate) {
    zip.file(`${slug}-task-register.csv`,  generateTaskRegisterCsv(gcc))
    zip.file(`${slug}-task-register.json`, generateTaskRegisterJson(gcc))
  }

  if (analyses.length > 0) {
    const tnFolder = zip.folder('tn-analyses')
    if (!tnFolder) throw new Error('Failed to create tn-analyses folder in ZIP')
    for (const a of analyses) {
      if (a.html === null) tnFolder.file(`${a.fileBase}.txt`, `TN analysis ${a.id} could not be fetched.`)
      else tnFolder.file(`${a.fileBase}.html`, protectGccHtml(a.html, gate))
    }
  }

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
}

/**
 * Page script of the Disposition, Sourcing Model, Sequencing and Value Case tabs.
 * Only the full export has them: the Simple (client) export leaves the code out
 * along with the data (WI 2822).
 */
function detailTabsJs(): string {
  return `
  var VERD  = {'Now':'b-red','Next':'b-orange','Later':'b-yellow','Never':'b-gray'};
  var EXIST = {'Keep':'b-green','Redesign':'b-yellow','Eliminate':'b-red','Automate':'b-blue','New':'b-purple'};
  var DISP  = {'Eliminate':'b-red','Automate':'b-blue','Agentify':'b-purple','Augment':'b-yellow','Human':'b-gray'};
  // Quick win, agentify first (WI 2806): same label and basis text as the in-app badge
  var QW_LABEL = ${JSON.stringify(GCC_QUICK_WIN_LABEL)};
  var QW_OFF = ${JSON.stringify(GCC_QUICK_WIN_OFF_SUMMARY)};
  var qwH = D.taggedTasks && D.taggedTasks.quickWinHorizonMonths;
  // WI 2886: a 0-month horizon turns quick wins off, so there is nothing to filter on
  var qwOn = qwH != null && qwH !== 0;
  function qwBadge(t) {
    if (!t.quickWin || t.monthsToImplement == null || qwH == null) return '';
    var m = t.monthsToImplement;
    var title = QW_LABEL+': Low complexity · '+m+' month'+(m===1?'':'s')+' ≤ '+qwH+'-month horizon';
    return ' <span class="badge b-green" title="'+h(title)+'" aria-label="'+h(title)+'">Quick win</span>';
  }

  // Disposition
  (function(){
    var rows = D.taggedTasks&&D.taggedTasks.taggedTasks||[];
    var srcList = D.sourcingModel&&D.sourcingModel.taskSourcing||[];
    var srcById = {};
    srcList.forEach(function(s){ srcById[s.taskId] = s; });
    function srcCell(r) {
      var s = srcById[r.taskId];
      if (s) return badge(pair(s.ownership,s.location), OWN[s.ownership]);
      return srcList.length ? '<span style="color:#d1d5db">&#8212;</span>' : bmap(TIER,r.sourcingTier);
    }
    var verdicts = uniq(rows.map(function(r){ return r.existenceVerdict; }));
    var disps    = uniq(rows.map(function(r){ return r.disposition; }));
    var AGENT_LABEL = ${JSON.stringify(GCC_AGENT_ESTIMATE_LABEL)};
    var filterGroups = [
      {key:'verdict',label:'Verdict',vals:verdicts},
      {key:'disp',   label:'Disposition',vals:disps},
      {key:'agent',  label:AGENT_LABEL,vals:['Yes','No']},
    ];
    if (qwOn) filterGroups.push({key:'qw',label:'Quick win',vals:['Yes','No']});
    var qwCount = rows.filter(function(r){ return r.quickWin; }).length;
    document.getElementById('pnl-disposition').innerHTML =
      filtersHtml('disp',filterGroups)+
      '<div class="tbl-wrap"><table><thead><tr>'+
      [th('Task'),th('Verdict'),th('Disposition'),th(AGENT_LABEL),th(srcList.length?'Sourcing':'Tier'),th('Source')].join('')+
      '</tr></thead><tbody>'+
      rows.map(function(r){
        var agent = r.agentMatch?'Yes':'No';
        return '<tr data-verdict="'+h(r.existenceVerdict)+'" data-disp="'+h(r.disposition)+'" data-agent="'+h(agent)+'" data-qw="'+(r.quickWin?'Yes':'No')+'">'+
          td(h(r.taskName)+qwBadge(r))+td(bmap(EXIST,r.existenceVerdict))+td(bmap(DISP,r.disposition))+
          td(badge(agent,r.agentMatch?'b-green':'b-gray'))+
          td(srcCell(r))+td(badge(r.signalSource,r.signalSource==='TN'?'b-blue':'b-gray'))+
          '</tr>';
      }).join('')+
      '</tbody></table></div>'+
      (qwH != null ? '<p class="qw-note" style="margin-top:8px;font-size:11px;color:#6b7280">'+
        (qwOn ? h(qwCount)+' quick win'+(qwCount===1?'':'s')+': low-complexity Automate / Agentify tasks live within '+h(qwH)+' months, agentified first' : h(QW_OFF))+
        '.</p>' : '');
    setupFilter('disp','pnl-disposition',qwOn ? ['verdict','disp','agent','qw'] : ['verdict','disp','agent']);
  })();

  // Sourcing Model
  (function(){
    var rows = D.sourcingModel&&D.sourcingModel.sourcingModel||[];
    var isV2 = rows.some(function(r){ return Array.isArray(r.allocation); });
    if (isV2) {
      // Automate first (WI 2048): tasks kept Retained because AI removes most of their work
      var AF_LABEL = ${JSON.stringify(GCC_AUTOMATE_FIRST_LABEL)};
      var afTasks = (D.sourcingModel.taskSourcing||[]).filter(function(t){ return t.automateFirst; });
      var afOf = function(r){ return afTasks.filter(function(t){ return t.capabilityName===r.capabilityName && t.functionScope===r.functionScope; }); };
      var afThreshold = D.sourcingModel.automateFirstThresholdPct;
      document.getElementById('pnl-sourcing').innerHTML =
        filtersHtml('src',[
          {key:'ownership',label:'Ownership',vals:['Captive-GCC','MSP','Retained']},
          {key:'location', label:'Location', vals:['HQ','Markets','Nearshore','Offshore']},
          {key:'hybrid',   label:'Hybrid',   vals:['Yes','No']},
        ])+
        '<div class="tbl-wrap"><table><thead><tr>'+
        [th('Capability'),th('Residual FTE'),th('Sourcing split'),th('Rationale')].join('')+
        '</tr></thead><tbody>'+
        rows.map(function(r){
          var parts = r.allocation||[];
          var af = afOf(r);
          var afNote = af.length ? '<div style="margin-top:4px;font-size:11px;color:#0B6690">'+h(AF_LABEL)+': '+
            af.map(function(t){ return h(t.taskName)+' ('+h(t.aiReleasedPct)+'% released by AI, '+h(fte2(t.residualFte))+' FTE stays '+h(pair('Retained',t.location))+')'; }).join('; ')+'</div>' : '';
          var split = parts.length ? parts.map(function(a){
            return badge(pair(a.ownership,a.location), OWN[a.ownership])+' '+h(fte2(a.fte))+' FTE ('+h(a.sharePct)+'%)';
          }).join('<br>') : '<span style="color:#9ca3af">No residual work</span>';
          return '<tr data-ownership-list="'+h(uniq(parts.map(function(a){ return a.ownership; })).join('|'))+'"'+
            ' data-location-list="'+h(uniq(parts.map(function(a){ return a.location; })).join('|'))+'"'+
            ' data-hybrid="'+(r.isHybrid?'Yes':'No')+'">'+
            td(h(r.capabilityName)+'<br><span style="color:#9ca3af;font-size:11px">'+h(r.functionScope)+'</span>'+(r.isHybrid?' '+badge('Hybrid','b-yellow'):'')+(af.length?' '+badge(af.length+' automate first','b-cyan'):''))+
            td(h(fte2(r.residualFte)))+td(split)+td(rat(r.rationale)+afNote)+'</tr>';
        }).join('')+
        '</tbody></table></div>'+
        (afThreshold!=null ? '<p style="margin-top:8px;font-size:11px;color:#6b7280">'+h(afTasks.length)+' task'+(afTasks.length===1?'':'s')+' automate first: AI releases '+h(afThreshold)+'% or more of the work, so it is not migrated and stays Retained where the role works today.</p>' : '');
      setupFilter('src','pnl-sourcing',['ownership','location','hybrid']);
      return;
    }
    var tiers   = uniq(rows.map(function(r){ return r.sourcingTier; }));
    var signals = uniq(rows.map(function(r){ return r.signalSource; }));
    document.getElementById('pnl-sourcing').innerHTML =
      filtersHtml('src',[
        {key:'tier',  label:'Tier',  vals:tiers},
        {key:'signal',label:'Signal',vals:signals},
      ])+
      '<div class="tbl-wrap"><table><thead><tr>'+
      [th('Capability'),th('Tier'),th('Signal'),th('Rationale')].join('')+
      '</tr></thead><tbody>'+
      rows.map(function(r){
        return '<tr data-tier="'+h(r.sourcingTier)+'" data-signal="'+h(r.signalSource)+'">'+
          td(h(r.capabilityName))+td(bmap(TIER,r.sourcingTier))+
          td(badge(r.signalSource,r.signalSource==='TN'?'b-blue':'b-gray'))+
          td(rat(r.rationale))+'</tr>';
      }).join('')+
      '</tbody></table></div>';
    setupFilter('src','pnl-sourcing',['tier','signal']);
  })();

  // Sequencing
  (function(){
    var rows = D.sequencing&&D.sequencing.sequencing||[];
    var isV2 = rows.some(function(r){ return Array.isArray(r.pathwaySplit); });
    if (isV2) {
      var PW = ${JSON.stringify(GCC_PATHWAY_LABELS)};
      var PWC = {'TransformFirst':'b-purple','ShiftFirst':'b-blue','ShiftAndTransform':'b-green','RetainAndTransform':'b-orange','ShiftOnly':'b-yellow','RetainAsIs':'b-gray'};
      var BANDS = ${JSON.stringify(Object.fromEntries(GCC_WAVES.map(w => [w, GCC_WAVE_BANDS[w].label])))};
      var seqTasks = D.sequencing.taskSequencing||[];
      var flow = D.sequencing.waveFlow||[];
      function pw(p) { return badge(PW[p]||p, PWC[p]); }
      function wv(w) { return w ? badge('W'+w,'b-gray') : '<span style="color:#d1d5db">&#8212;</span>'; }
      var present = Object.keys(PW).filter(function(p){ return seqTasks.some(function(t){ return t.pathway===p; }); }).map(function(p){ return PW[p]; });
      document.getElementById('pnl-sequencing').innerHTML =
        filtersHtml('seq',[
          {key:'pathway',label:'Pathway',  vals:present},
          {key:'twave',  label:'AI Transform',vals:['W1','W2','W3']},
          {key:'mwave',  label:'Migration',vals:['W1','W2','W3']},
          {key:'mixed',  label:'Mixed',    vals:['Yes','No']},
        ])+
        '<div class="tbl-wrap"><table><thead><tr>'+
        [th('Capability'),th('Pathway'),th('AI Transform'),th('Migration'),th('Urgency'),th('Rationale')].join('')+
        '</tr></thead><tbody>'+
        rows.map(function(r){
          var split = r.pathwaySplit||[];
          var mine = seqTasks.filter(function(t){ return t.functionScope===r.functionScope && t.capabilityName===r.capabilityName; });
          var splitHtml = r.isMixed ? '<br>'+split.map(function(s){ return '<span style="font-size:11px;color:#6b7280">'+h(PW[s.pathway]||s.pathway)+' '+h(s.sharePct)+'%</span>'; }).join('<br>') : '';
          var taskHtml = mine.length ? '<details><summary>'+mine.length+' task'+(mine.length!==1?'s':'')+'</summary><div class="rat-body">'+
            mine.map(function(t){
              return h(t.taskName)+qwBadge(t)+' &middot; '+h(t.disposition)+' &middot; '+h(pair(t.ownership,t.location))+' &middot; '+pw(t.pathway)+
                ' T '+(t.transformWave?'W'+t.transformWave:'&#8212;')+' / M '+(t.migrationWave?'W'+t.migrationWave:'&#8212;')+
                ' (AI ready '+h(t.aiReadiness)+', move ready '+h(t.transitionFeasibility)+(t.defaulted?', defaulted':'')+')';
            }).join('<br>')+'</div></details>' : '';
          return '<tr data-pathway-list="'+h(split.map(function(s){ return PW[s.pathway]; }).join('|'))+'"'+
            ' data-twave="'+(r.transformWave?'W'+r.transformWave:'')+'" data-mwave="'+(r.migrationWave?'W'+r.migrationWave:'')+'"'+
            ' data-mixed="'+(r.isMixed?'Yes':'No')+'">'+
            td(h(r.capabilityName)+'<br><span style="color:#9ca3af;font-size:11px">'+h(r.functionScope)+'</span>'+(r.isMixed?' '+badge('Mixed','b-yellow'):''))+
            td((r.pathway?pw(r.pathway):'')+splitHtml)+td(wv(r.transformWave))+td(wv(r.migrationWave))+
            '<td>'+urg(r.urgencyScore)+'</td>'+td(rat(r.rationale)+taskHtml)+'</tr>';
        }).join('')+
        '</tbody></table></div>'+
        '<div class="tbl-wrap" style="margin-top:16px"><table><thead><tr>'+
        [th('Wave'),th('AI transformation (FTE released)'),th('Migration (FTE moved)')].join('')+
        '</tr></thead>'+
        flow.map(function(w){
          return '<tr>'+td('<b>Wave '+h(w.wave)+'</b> <span style="color:#9ca3af">'+h(BANDS[w.wave])+'</span>')+td(h(fte2(w.transformFte)))+td(h(fte2(w.migrationFte)))+'</tr>';
        }).join('')+
        '</table></div>'+
        (function(){
          var qw = seqTasks.filter(function(t){ return t.quickWin; }).length;
          return qw ? '<p class="qw-note" style="margin-top:8px;font-size:11px;color:#6b7280">'+h(qw)+' quick win'+(qw===1?'':'s')+' agentified first: AI transformation no later than the wave its months to implement fall in, and any migration after it.</p>' : '';
        })();
      setupFilter('seq','pnl-sequencing',['pathway','twave','mwave','mixed']);
      return;
    }
    var shifts = uniq(rows.map(function(r){ return r.shiftVerdict; }));
    var tforms = uniq(rows.map(function(r){ return r.transformVerdict; }));
    var waves  = uniq(rows.map(function(r){ return String(r.waveAssignment); }));
    document.getElementById('pnl-sequencing').innerHTML =
      filtersHtml('seq',[
        {key:'shift',label:'Shift',   vals:shifts},
        {key:'tform',label:'Transform',vals:tforms},
        {key:'wave', label:'Wave',    vals:waves},
      ])+
      '<div class="tbl-wrap"><table><thead><tr>'+
      [th('Capability'),th('Shift'),th('Transform'),th('Urgency'),th('Wave'),th('Rationale')].join('')+
      '</tr></thead><tbody>'+
      rows.map(function(r){
        return '<tr data-shift="'+h(r.shiftVerdict)+'" data-tform="'+h(r.transformVerdict)+'" data-wave="'+h(r.waveAssignment)+'">'+
          td(h(r.capabilityName))+td(bmap(VERD,r.shiftVerdict))+td(bmap(VERD,r.transformVerdict))+
          '<td>'+urg(r.urgencyScore)+'</td>'+
          td(badge('W'+r.waveAssignment,'b-gray'))+
          td(rat(r.rationale))+'</tr>';
      }).join('')+
      '</tbody></table></div>';
    setupFilter('seq','pnl-sequencing',['shift','tform','wave']);
  })();

  // Value Case (WI 2326)
  (function(){
    var vc = D.valueCase;
    if (!vc) return;
    var OWNL = {'Captive-GCC':'Captive GCC','MSP':'MSP','Retained':'Retained'};
    var VBANDS = ${JSON.stringify(Object.fromEntries(GCC_WAVES.map(w => [w, GCC_WAVE_BANDS[w].label])))};
    var MATCH = {'exact':'Exact title','band':'Role band','floor':'Analyst floor','missing':'No rate'};
    var VT = ${JSON.stringify(VALUE_CASE_TIPS)};
    function usd(n) { var v = Math.round(+n||0); return (v<0?'−':'')+'$'+Math.abs(v).toLocaleString('en-US'); }
    function rateOf(n) { return n==null ? 'No rate' : usd(n); }
    function owners(f) {
      return ['Captive-GCC','MSP','Retained'].filter(function(o){ return (f&&f[o])>0; })
        .map(function(o){ return OWNL[o]+' '+fte2(f[o]); }).join(' · ') || '&#8212;';
    }
    function figs(f) { return td(usd(f.currentCostUSD))+td(usd(f.aiSavingUSD))+td(usd(f.locationSavingUSD))+td('<b>'+usd(f.totalSavingUSD)+'</b>')+td(usd(f.endStateCostUSD)); }
    var t = vc.totals||{};
    var chain = [
      ['Current FTE', fte2(t.currentFte), VT.valueCaseCurrentFte],
      ['AI released FTE', '−'+fte2(t.aiReleasedFte), VT.valueCaseAiReleasedFte],
      ['Residual FTE', fte2(t.residualFte), VT.valueCaseResidualFte],
      ['Future workforce', owners(t.futureFteByOwnership), VT.valueCaseFutureFte],
      ['Current cost', usd(t.currentCostUSD), VT.valueCaseCurrentCost],
      ['AI productivity saving', '−'+usd(t.aiSavingUSD), VT.valueCaseAiSaving],
      ['Location arbitrage saving', '−'+usd(t.locationSavingUSD), VT.valueCaseLocationSaving],
      ['Net value case (run-rate saving)', '<b>'+usd(t.totalSavingUSD)+'</b>', VT.valueCaseTotalSaving],
      ['End-state cost', usd(t.endStateCostUSD), VT.valueCaseEndStateCost],
    ];
    var caps = vc.capabilities||[];
    var lines = vc.tasks||[];
    var a = vc.assumptions||{};
    var flags = [];
    if ((a.defaultedLocationRoles||[]).length) flags.push('No current location given, so priced at HQ: '+h(a.defaultedLocationRoles.join('; '))+'.');
    if ((a.unpricedTaskIds||[]).length) flags.push('No current rate in the table, so left out of the cost lines: '+h(a.unpricedTaskIds.join(', '))+'.');
    if ((a.endRateFallbackTaskIds||[]).length) flags.push('No end-state rate in the table, so priced at today’s rate (no location saving): '+h(a.endRateFallbackTaskIds.join(', '))+'.');
    document.getElementById('pnl-valuecase').innerHTML =
      '<div class="tbl-wrap"><table><thead><tr>'+th('Traceability chain',VT.valueCaseWaterfall)+th('Total',VT.valueCaseChainTotal)+'</tr></thead><tbody>'+
      chain.map(function(c){ return '<tr><td'+tip([c[0],c[2]])+'>'+h(c[0])+'</td>'+td(c[1])+'</tr>'; }).join('')+
      '</tbody></table></div>'+
      '<div class="tbl-wrap" style="margin-top:16px"><table><thead><tr>'+
      [th('Capability',VT.valueCaseCapability),th('Current FTE',VT.valueCaseCurrentFte),th('AI released',VT.valueCaseAiReleasedFte),th('Residual',VT.valueCaseResidualFte),
       th('Sourcing split',VT.sourcingSplit),th('Future FTE',VT.valueCaseFutureFte),th('Current cost',VT.valueCaseCurrentCost),th('AI saving',VT.valueCaseAiSaving),
       th('Location saving',VT.valueCaseLocationSaving),th('Total saving',VT.valueCaseTotalSaving),th('End-state cost',VT.valueCaseEndStateCost)].join('')+
      '</tr></thead><tbody>'+
      caps.map(function(c){
        var mine = lines.filter(function(l){ return l.functionScope===c.functionScope && l.capabilityName===c.capabilityName; });
        var split = (c.sourcingSplit||[]).map(function(s){ return badge(pair(s.ownership,s.location), OWN[s.ownership])+' '+h(fte2(s.fte)); }).join('<br>') || '<span style="color:#9ca3af">No residual work</span>';
        var taskHtml = mine.length ? '<details><summary>'+mine.length+' task'+(mine.length!==1?'s':'')+'</summary><div class="rat-body">'+
          mine.map(function(l){
            return h(l.taskName)+' &middot; '+h(l.disposition)+' &middot; '+h(l.currentTier)+' → '+h(pair(l.ownership,l.location))+
              ' &middot; '+h(fte2(l.baselineFte))+' → '+h(fte2(l.residualFte))+' FTE &middot; '+h(rateOf(l.currentRateUSD))+' → '+h(rateOf(l.endRateUSD))+
              ' &middot; AI '+h(usd(l.aiSavingUSD))+' (T-W'+h(l.transformWave||3)+') + location '+h(usd(l.locationSavingUSD))+' (M-W'+h(l.migrationWave||3)+')';
          }).join('<br>')+'</div></details>' : '';
        return '<tr>'+td(h(c.capabilityName)+'<br><span style="color:#9ca3af;font-size:11px">'+h(c.functionScope)+'</span>'+taskHtml)+
          td(h(fte2(c.currentFte)))+td('−'+h(fte2(c.aiReleasedFte)))+td(h(fte2(c.residualFte)))+td(split)+td(owners(c.futureFteByOwnership))+figs(c)+'</tr>';
      }).join('')+
      '<tr>'+td('<b>Total</b>')+td(h(fte2(t.currentFte)))+td('−'+h(fte2(t.aiReleasedFte)))+td(h(fte2(t.residualFte)))+td('')+td(owners(t.futureFteByOwnership))+figs(t)+'</tr>'+
      '</tbody></table></div>'+
      '<div class="tbl-wrap" style="margin-top:16px"><table><thead><tr>'+
      [th('Wave',VT.valueCaseWaves),th('AI saving starts',VT.valueCaseWaveAiStart),th('Location saving starts',VT.valueCaseWaveLocationStart),th('Run-rate after wave',VT.valueCaseWaveRunRate)].join('')+
      '</tr></thead><tbody>'+
      (vc.waves||[]).map(function(w){
        return '<tr>'+td('<b>Wave '+h(w.wave)+'</b> <span style="color:#9ca3af">'+h(VBANDS[w.wave])+'</span>')+td(usd(w.aiSavingUSD))+td(usd(w.locationSavingUSD))+td('<b>'+usd(w.cumulativeSavingUSD)+'</b>')+'</tr>';
      }).join('')+
      '</tbody></table></div>'+
      '<div class="card" style="margin-top:16px;font-size:12px;color:#374151;line-height:1.6">'+
      '<div style="font-weight:700;margin-bottom:6px">Assumptions</div>'+
      '<ul style="padding-left:16px">'+(a.notes||[]).map(function(n){ return '<li>'+h(n)+'</li>'; }).join('')+
      flags.map(function(f){ return '<li style="color:#b45309">'+f+'</li>'; }).join('')+'</ul>'+
      '<div class="tbl-wrap" style="margin-top:10px"><table><thead><tr>'+[th('Tier',VT.valueCasePayTier),th('Role',VT.valueCasePayRole),th('Annual rate',VT.valueCasePayRate),th('Match',VT.valueCasePayMatch)].join('')+'</tr></thead><tbody>'+
      (a.payRates||[]).map(function(r){ return '<tr>'+td(bmap(TIER,r.tier))+td(h(r.roleTitle))+td(h(rateOf(r.annualRateUSD)))+td(h(MATCH[r.match]||r.match))+'</tr>'; }).join('')+
      '</tbody></table></div></div>';
  })();

`
}

/**
 * Generate a standalone HTML document embedding GCC dashboard data.
 * Self-contained — no external dependencies, suitable for offline viewing.
 * A connect-src 'none' CSP is injected to block network exfiltration.
 */
export function generateGccHtml(gccData: GccDashboardData, project: Project, options: { variant?: GccExportVariant } = {}): string {
  const simple = options.variant === 'simple'
  // The export never renders the Pipeline Trace, and the trace holds the internal
  // system prompt and raw model responses — keep it out of client-facing files.
  const { apiTrace: _apiTrace, ...rest } = gccData
  // Match the in-app Disposition tab: tasks excluded in the S0b review are left out.
  const filtered = rest.taggedTasks
    ? { ...rest, taggedTasks: { ...rest.taggedTasks, taggedTasks: filterExcludedTaggedTasks(gccData) } }
    : rest
  // The Simple (client) export embeds only what its tabs draw (WI 2822)
  const exportData = simple
    ? toSimpleGccData(filtered)
    : { ...filtered, agentBuilds: gccAgentBuilds(filtered), workView: gccWorkView(filtered), roleEvolution: gccRoleEvolution(filtered) }
  const projectMeta = {
    id: project.id,
    client: project.client,
    industry: project.industry,
    branding: project.branding,
    gccScenario: project.gccScenario,
  }
  const palette = gccExecutivePaletteCss(project.branding?.primaryColor, project.branding?.accentColor)
  const generatedDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  const waveBands = JSON.stringify(Object.fromEntries(GCC_WAVES.map(w => [w, GCC_WAVE_BANDS[w].label])))
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self'; connect-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'">
  <title>${escapeHtml(project.client ?? '')} — Global Capability Center Analysis</title>
  <style>${GCC_EXPORT_CSS}
${palette}
  </style>
</head>
<body>
  <script>
    window.__GCC_DATA__ = ${escapeJson(exportData)};
    window.__GCC_PROJECT__ = ${escapeJson(projectMeta)};
  </script>
  <header class="hero" id="gcc-hero"></header>
  <nav class="tab-nav" aria-label="Dashboard sections"><div class="tab-bar"><div class="tab-rail"><button class="tab-arrow l" id="gcc-tabs-l" type="button" tabindex="-1" aria-label="Scroll tabs left">&#8249;</button><div class="tab-nav-inner" id="gcc-tabs" role="tablist"></div><button class="tab-arrow r" id="gcc-tabs-r" type="button" tabindex="-1" aria-label="Scroll tabs right">&#8250;</button></div><div class="appx" id="gcc-appx"><button class="appx-btn" id="gcc-appx-btn" type="button" aria-haspopup="menu" aria-expanded="false" aria-controls="gcc-appx-menu">Appendix<span class="appx-chev" aria-hidden="true">&#9662;</span></button><div class="appx-menu" id="gcc-appx-menu" role="menu" aria-label="Appendix" hidden></div></div></div></nav>
  <main id="gcc-panels"></main>
  <footer class="footer"><span>Generated by Talent Navigator &middot; Global Capability Center Design Engine &middot; ${escapeHtml(generatedDate)}</span><span>Figures are calculated from the saved Global Capability Center design. Annual run-rate labour cost.</span></footer>
  <script>
(function(){
  var D = window.__GCC_DATA__;
  var P = window.__GCC_PROJECT__;

  function h(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }
  function fmtAuto(n) {
    var v = +n||0;
    if (Math.abs(v) >= 1e6) return '$' + (v/1e6).toFixed(1) + 'M';
    if (Math.abs(v) >= 1e3) return '$' + (v/1e3).toFixed(0) + 'K';
    return '$' + v.toFixed(0);
  }
  function fmtM(n) { return '$' + ((+n||0)/1e6).toFixed(1) + 'M'; }
  // Headcounts are FTE to 0.01: show at most two decimals so float noise never reaches the page
  function fmtN(n) { return (+n||0).toLocaleString('en-US', { maximumFractionDigits: 2 }); }
  function uniq(arr) { return arr.filter(function(v,i,a){ return v!=null && v!=='' && a.indexOf(v)===i; }); }

  var TIER  = {'HQ':'b-blue','Markets':'b-purple','GCC-Offshore':'b-green','GCC-Nearshore':'b-yellow','MSP':'b-orange'};
  var SEG   = {'GCC-eligible':'b-green','Retained':'b-blue'};
  var RTAG  = {'Retained':'b-blue','Evolved':'b-yellow','Consolidated':'b-orange','New':'b-green'};
  var OWN   = {'Captive-GCC':'b-cyan','MSP':'b-orange','Retained':'b-blue'};
  function pair(o, l) { return (o==='Captive-GCC'?'Captive GCC':o)+' · '+l; }
  function fte2(n) { return String(Math.round((+n||0)*100)/100); }

  function badge(text, cls) { return '<span class="badge '+(cls||'b-gray')+'">'+h(text)+'</span>'; }
  function bmap(map, val) { return badge(val, map[val]); }
  function urg(n) {
    var brand = getComputedStyle(document.documentElement).getPropertyValue('--brand').trim() || '#A100FF';
    var s = '';
    for (var i=1;i<=3;i++) s += '<span class="dot" style="background:'+(i<=n?brand:'#e5e7eb')+'"></span>';
    return s;
  }
  /** A header cell; with \`t\` it carries a hover / focus tooltip headed by its label (WI 2577). */
  function th(c, t) { return '<th'+(t ? tip([c, t]) : '')+'>'+h(c)+'</th>'; }
  function td(c) { return '<td>'+c+'</td>'; }
  function rat(text) {
    if (!text) return '<span style="color:#d1d5db">&#8212;</span>';
    var full = h(text);
    if (text.length <= 60) return '<span style="font-size:12px;color:#374151">'+full+'</span>';
    var short = h(text.slice(0,60))+'&hellip;';
    return '<details><summary>'+short+'</summary><div class="rat-body">'+full+'</div></details>';
  }

  var BANDS_W = ${waveBands};
  var AGENT_EST = ${escapeJson(GCC_AGENT_ESTIMATE_LABEL)};
  var AGENT_NOTE = ${escapeJson(GCC_AGENT_CATALOG_UNAVAILABLE_NOTE)};
${GCC_EXPORT_STORY_JS}
  renderHero();

  // Tab system (WI 2881): five numbered story tabs, the same in both downloads, then every
  // other view under the Appendix menu. \`intro\` is what the view's Intro card says it shows.
  var tabs = [
    {id:'intro', label:'Intro', story:true, title:'Reinventing work with AI and a Global Capability Center', sub:INTRO_PURPOSE},
    {id:'overview', label:'Art of the Possible', story:true, title:'The Art of the Possible',
     sub:'AI + GCC: a reinvention of work, workforce and value — from today’s in-market model to an AI-enabled, globally delivered one.',
     intro:'Today, tomorrow and the end state: the workforce, the capacity AI releases and the run-rate labour cost.'},
    {id:'workview', label:'Work View', story:true, title:'How the work is changing',
     sub:'The functions, capabilities and tasks in scope, and what happens to each one’s work.',
     intro:'The functions, capabilities and tasks in scope, and how each one’s work changes.'},
    // WI 2822: views brought in from the original TN dashboard, in both exports
    {id:'roleevo', label:'Role Evolution', story:true, title:'How roles evolve',
     sub:'How today’s roles become the end-state roles, and the capacity AI releases. Choose a role to see the skills moving away, coming in and net-new.',
     intro:'How today’s roles become the end-state roles, and the skills they move away from and build.'},
    {id:'agents', label:'Agent Marketplace', story:true, title:'Automation & AI builds',
     sub:'The work AI takes on: agents, automation and AI assistance, task by task, with the capacity each releases.',
     intro:'The tasks AI takes on: agents, automation and AI assistance, with the capacity each releases.'},
    {id:'process', label:'Process View', title:'Process view',
     sub:'Each process in today’s baseline, task by task, with what happens to its work.'},
    {id:'levers', label:'Value Levers', title:'How value is created',
     sub:D.valueCase
       ? 'AI releases capacity first; the GCC then moves the remaining work to a scalable, lower-cost base.'
       : 'Run-rate labour savings across the GCC-eligible and retained populations.'},
  ];
  // Designs saved before WI 2326 have no value ledger, so they export without the ledger-based views
  if (D.valueCase) tabs.push({id:'scenario', label:'Scenario Explorer', title:'Explore different futures',
    sub:'Adjust how much of the AI and GCC levers is realised and see the effect on workforce and cost.'});
  tabs.push({id:'roles', label:'End State Roles', title:'End-state roles',
    sub:'Headcount, cost and skills of every role in the future workforce.'});
${simple ? '' : `  tabs.push(
    {id:'disposition', label:'Disposition', title:'AI disposition by task',
     sub:'Each task tagged Eliminate, Automate, Agentify, Augment or Human, with its sourcing.'},
    {id:'sourcing', label:'Sourcing Model', title:'Residual sourcing model',
     sub:'Ownership × location of the work left for people, per capability.'},
    {id:'sequencing', label:'Sequencing', title:'Sequencing and waves',
     sub:'Pathways, AI transformation and migration waves, and urgency per capability.'}
  );
  if (D.valueCase) tabs.push({id:'valuecase', label:'Value Case', title:'Value case ledger',
    sub:'Every figure traced from task-level FTE and pay rates, with AI and location savings kept separate.'});`}
  var setTab = buildTabs(tabs);
  initTooltips();

  // Value Case cards
  function vcCard(pop, label, mod) {
    var d = pop||{};
    return '<div class="vc-card lift vc-card-'+mod+'">'+
      '<div class="vc-card-name">'+h(label)+'</div>'+
      '<div class="vc-big">'+fFte(d.totalHeadcountEndState)+'</div>'+
      '<div class="vc-big-lbl">End-state HC</div>'+
      '<div class="vc-saving">'+fmtAuto(d.labourSavingUSD)+' Labour saving</div>'+
      '<div class="vc-row"><span>Today HC</span><span class="vc-row-val">'+fFte(d.totalHeadcountToday)+'</span></div>'+
      '<div class="vc-row"><span>Cost today</span><span class="vc-row-val">'+fmtAuto(d.totalLabourCostTodayUSD)+'</span></div>'+
      '<div class="vc-row"><span>Cost end-state</span><span class="vc-row-val">'+fmtAuto(d.totalLabourCostEndStateUSD)+'</span></div>'+
      (d.aiSavingUSD!=null && d.locationSavingUSD!=null
        ? '<div class="vc-row"><span>AI / location saving</span><span class="vc-row-val">'+fmtAuto(d.aiSavingUSD)+' / '+fmtAuto(d.locationSavingUSD)+'</span></div>'
        : '')+
      '</div>';
  }

  // Intro, with the 5-step method, in both downloads (WI 2881)
  safe(function(){ renderIntro(tabs, setTab); });
  safe(renderOverview);
  var vcEl = document.getElementById('gcc-vc');
  if (vcEl) vcEl.innerHTML =
    vcCard(D.gccPopulation,'GCC-Eligible','gcc')+
    vcCard(D.retainedPopulation,'Retained','ret')+
    vcCard(D.combined,'Combined','comb');
  safe(renderLevers);
  safe(renderWorkView);
  safe(renderProcessView);
  safe(renderRoleEvolution);
  safe(renderAgentMarketplace);
  if (D.valueCase) safe(renderScenario);

  // Filter helpers. A group with \`counts\` shows how many items each pill keeps (WI 2822).
  function filtersHtml(ns,groups) {
    var html = '<div class="filters" id="f-'+ns+'">';
    groups.forEach(function(g){
      var n = function(v){ return g.counts ? ' ('+(g.counts[v]||0)+')' : ''; };
      html += '<span class="f-lbl">'+h(g.label)+':</span>';
      html += '<button class="pill active" data-key="'+h(g.key)+'" data-val="">All'+(g.counts ? ' ('+g.total+')' : '')+'</button>';
      g.vals.forEach(function(v){ html += '<button class="pill" data-key="'+h(g.key)+'" data-val="'+h(v)+'">'+h(v)+n(v)+'</button>'; });
    });
    return html+'</div>';
  }
  function setupFilter(ns,panelId,keys) {
    var active = {};
    keys.forEach(function(k){ active[k]=''; });
    var fDiv = document.getElementById('f-'+ns);
    if (!fDiv) return;
    fDiv.addEventListener('click',function(e){
      var btn = e.target;
      if (!btn.classList||!btn.classList.contains('pill')) return;
      var key = btn.getAttribute('data-key');
      var val = btn.getAttribute('data-val');
      if (key===null) return;
      active[key]=val;
      fDiv.querySelectorAll('[data-key="'+key+'"]').forEach(function(p){ p.classList.remove('active'); });
      btn.classList.add('active');
      // Table rows, or the cards of the Role Evolution and Agent Marketplace tabs
      document.querySelectorAll('#'+panelId+' tbody tr, #'+panelId+' [data-card]').forEach(function(row){
        var show = keys.every(function(k){
          if (!active[k]||row.getAttribute('data-'+k)===active[k]) return true;
          var list = row.getAttribute('data-'+k+'-list');
          return !!list && list.split('|').indexOf(active[k])>=0;
        });
        row.classList.toggle('hidden',!show);
      });
    });
  }

${simple ? '' : detailTabsJs()}
  // End State Roles
  (function(){
    var gccR = (D.gccPopulation&&D.gccPopulation.endStateRoles||[]).map(function(r){ return Object.assign({},r,{_seg:'GCC-eligible'}); });
    var retR = (D.retainedPopulation&&D.retainedPopulation.endStateRoles||[]).map(function(r){ return Object.assign({},r,{_seg:'Retained'}); });
    var rows = gccR.concat(retR);
    var segs = uniq(rows.map(function(r){ return r._seg; }));
    var tiers= uniq(rows.map(function(r){ return r.sourcingTier; }));
    var tags = uniq(rows.map(function(r){ return r.tag; }));
    document.getElementById('pnl-roles').innerHTML =
      filtersHtml('rol',[
        {key:'seg', label:'Segment',vals:segs},
        {key:'tier',label:'Tier',   vals:tiers},
        {key:'tag', label:'Tag',    vals:tags},
      ])+
      '<div class="tbl-wrap"><table><thead><tr>'+
      [th('Role'),th('Segment'),th('Tag'),th('Tier'),th('HC Today'),th('HC End-State'),th('Delta'),th('Labour Saving')].join('')+
      '</tr></thead><tbody>'+
      rows.map(function(r){
        var delta  = +fte2((r.headcountEndState||0)-(r.headcountToday||0));
        // The Simple export embeds each role's saving instead of its two labour costs (WI 2822)
        var saving = r.labourSavingUSD!=null ? r.labourSavingUSD : (r.labourCostTodayUSD||0)-(r.labourCostEndStateUSD||0);
        var dc = delta<0?'#15803d':delta>0?'#b91c1c':'#6b7280';
        var sc = saving>0?'#15803d':'#b91c1c';
        return '<tr data-seg="'+h(r._seg)+'" data-tier="'+h(r.sourcingTier)+'" data-tag="'+h(r.tag)+'">'+
          td(h(r.roleName))+td(bmap(SEG,r._seg))+td(bmap(RTAG,r.tag))+td(bmap(TIER,r.sourcingTier))+
          td(fmtN(r.headcountToday))+td(fmtN(r.headcountEndState))+
          '<td style="font-weight:600;color:'+dc+'">'+(delta>0?'+':'')+delta+'</td>'+
          '<td style="font-weight:600;color:'+sc+'">'+fmtM(saving)+'</td>'+
          '</tr>';
      }).join('')+
      '</tbody></table></div>';
    setupFilter('rol','pnl-roles',['seg','tier','tag']);
  })();

  setTab(0);
  if (MOTION) countUp(document.getElementById('gcc-hero'));
})();
  </script>
</body>
</html>`
}
